import { useEffect, useMemo, useState } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';
import type { FluxogramaNode as ApiNode, FluxogramaNodeStyle } from '@/types/fluxograma';
import { SHAPE_META } from './shapeMeta';
import { ICON_MAP, ICON_OPTIONS } from './iconLibrary';
import { FiguraPicker } from '@/components/figuras/FiguraPicker';

export interface PanelPessoa {
  id: string;
  nome: string;
}

export interface PanelDepartamento {
  id: string;
  nome: string;
}

export interface PanelProjeto {
  id: string;
  titulo: string;
}

interface NodePropertiesPanelProps {
  node: ApiNode;
  pessoas: PanelPessoa[];
  departamentos: PanelDepartamento[];
  projetos: PanelProjeto[];
  onChange: (updates: Partial<ApiNode>) => void;
  onClose: () => void;
  readonly?: boolean;
}

export function NodePropertiesPanel({
  node,
  pessoas,
  departamentos,
  projetos,
  onChange,
  onClose,
  readonly,
}: NodePropertiesPanelProps) {
  // Defesa: shape pode chegar inválido em estados transientes; cai num fallback seguro.
  const meta = SHAPE_META[node.shape] ?? SHAPE_META.process;
  // Forma "Figura": sem preenchimento/moldura; a cor da "borda" pinta o ícone e o seletor de figura entra no lugar
  // de "Imagem (URL)" e "Ícone".
  const ehFigura = node.shape === 'figura';
  // Figura sem cor escolhida segue a cor do tema: o padrão vazio não é gravado pelo commitStyle.
  const corPadraoBorda = ehFigura ? '' : meta.defaultColor;
  const corPadraoTexto = ehFigura ? '' : '#0F172A';

  const [label, setLabel] = useState(node.label ?? '');
  const [descricao, setDescricao] = useState(node.descricao || '');
  const [bgColor, setBgColor] = useState(node.style?.bg_color || '');
  const [borderColor, setBorderColor] = useState(node.style?.border_color || corPadraoBorda);
  const [textColor, setTextColor] = useState(node.style?.text_color || corPadraoTexto);
  const [borderWidth, setBorderWidth] = useState<number>(node.style?.border_width ?? 2);
  const [fontSize, setFontSize] = useState<number>(node.style?.font_size ?? 13);
  const [icon, setIcon] = useState<string>(node.style?.icon || '');
  const [imageUrl, setImageUrl] = useState<string>(node.style?.image_url || '');

  useEffect(() => {
    const safeMeta = SHAPE_META[node.shape] ?? SHAPE_META.process;
    setLabel(node.label ?? '');
    setDescricao(node.descricao || '');
    setBgColor(node.style?.bg_color || '');
    // Figura: vazio = cor do tema (mesma regra de corPadraoBorda/corPadraoTexto).
    setBorderColor(node.style?.border_color || (node.shape === 'figura' ? '' : safeMeta.defaultColor));
    setTextColor(node.style?.text_color || (node.shape === 'figura' ? '' : '#0F172A'));
    setBorderWidth(node.style?.border_width ?? 2);
    setFontSize(node.style?.font_size ?? 13);
    setIcon(node.style?.icon || '');
    setImageUrl(node.style?.image_url || '');
  }, [node.id, node.label, node.descricao, node.style, node.shape]);

  const responsavelLabel = useMemo(
    () => pessoas.find((p) => p.id === node.responsavel_node_id)?.nome,
    [pessoas, node.responsavel_node_id],
  );

  // Constrói o objeto style a partir do estado atual e emite a mudança.
  const commitStyle = (overrides?: Partial<FluxogramaNodeStyle>) => {
    const style: FluxogramaNodeStyle = { ...(node.style || {}) };
    const bg = overrides?.bg_color ?? bgColor;
    const bd = overrides?.border_color ?? borderColor;
    const tx = overrides?.text_color ?? textColor;
    const bw = overrides?.border_width ?? borderWidth;
    const fs = overrides?.font_size ?? fontSize;
    const ic = overrides?.icon ?? icon;
    const img = overrides?.image_url ?? imageUrl;
    if (bg) style.bg_color = bg;
    else delete style.bg_color;
    if (bd) style.border_color = bd;
    else delete style.border_color;
    if (tx) style.text_color = tx;
    else delete style.text_color;
    if (bw) style.border_width = bw;
    else delete style.border_width;
    if (fs) style.font_size = fs;
    else delete style.font_size;
    if (ic) style.icon = ic;
    else delete style.icon;
    if (img) style.image_url = img;
    else delete style.image_url;
    onChange({ style });
  };

  const handleIconSelect = (next: string) => {
    setIcon(next);
    commitStyle({ icon: next });
  };

  const handleColorBlur = () => commitStyle();

  return (
    <aside className="w-80 border-l bg-background flex flex-col">
      <header className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {meta.label}
          </div>
          <div className="text-sm font-semibold truncate max-w-[200px]">{node.label || 'Sem título'}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <Label htmlFor="prop-label">Texto</Label>
          <Input
            id="prop-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => label !== node.label && onChange({ label })}
            disabled={readonly}
          />
        </div>

        <div>
          <Label htmlFor="prop-desc">Descrição</Label>
          <Textarea
            id="prop-desc"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            onBlur={() => {
              const next = descricao.trim() || null;
              if (next !== node.descricao) onChange({ descricao: next });
            }}
            rows={3}
            disabled={readonly}
          />
        </div>

        {ehFigura && (
          <FiguraPicker
            empresaId={node.empresa_id}
            value={node.style?.figura}
            disabled={readonly}
            onChange={(figura) => onChange({ style: { ...(node.style || {}), figura } })}
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          {!ehFigura && (
          <div>
            <Label htmlFor="prop-bg">Preenchimento</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                id="prop-bg"
                type="color"
                value={bgColor || '#ffffff'}
                onChange={(e) => setBgColor(e.target.value)}
                onBlur={handleColorBlur}
                disabled={readonly}
                className="h-9 w-12 rounded border bg-background cursor-pointer"
              />
              <Input
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                onBlur={handleColorBlur}
                placeholder="#FFFFFF"
                disabled={readonly}
                className="h-9"
              />
            </div>
          </div>
          )}
          <div>
            <Label htmlFor="prop-border">{ehFigura ? 'Cor do ícone' : 'Borda'}</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                id="prop-border"
                type="color"
                value={borderColor || '#000000'}
                onChange={(e) => setBorderColor(e.target.value)}
                onBlur={handleColorBlur}
                disabled={readonly}
                className="h-9 w-12 rounded border bg-background cursor-pointer"
              />
              <Input
                value={borderColor}
                onChange={(e) => setBorderColor(e.target.value)}
                onBlur={handleColorBlur}
                placeholder="#000000"
                disabled={readonly}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="prop-text-color">Cor do texto</Label>
          <div className="flex items-center gap-2 mt-1">
            <input
              id="prop-text-color"
              type="color"
              value={textColor || '#0F172A'}
              onChange={(e) => setTextColor(e.target.value)}
              onBlur={() => commitStyle()}
              disabled={readonly}
              className="h-9 w-12 rounded border bg-background cursor-pointer"
            />
            <Input
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              onBlur={() => commitStyle()}
              placeholder="#0F172A"
              disabled={readonly}
              className="h-9"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {!ehFigura && (
          <div>
            <Label htmlFor="prop-border-width">Espessura da borda</Label>
            <Input
              id="prop-border-width"
              type="number"
              min={0}
              max={8}
              value={borderWidth}
              onChange={(e) => setBorderWidth(Number(e.target.value))}
              onBlur={() => commitStyle({ border_width: borderWidth })}
              disabled={readonly}
              className="h-9 mt-1"
            />
          </div>
          )}
          <div>
            <Label htmlFor="prop-font-size">Tamanho da fonte</Label>
            <Input
              id="prop-font-size"
              type="number"
              min={10}
              max={32}
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              onBlur={() => commitStyle({ font_size: fontSize })}
              disabled={readonly}
              className="h-9 mt-1"
            />
          </div>
        </div>

        {!ehFigura && (<>
        <div>
          <Label htmlFor="prop-image-url">Imagem (URL)</Label>
          <div className="flex items-center gap-2 mt-1">
            {imageUrl && (
              <img
                src={imageUrl}
                alt=""
                referrerPolicy="no-referrer"
                className="h-9 w-9 rounded border object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
            )}
            <Input
              id="prop-image-url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onBlur={() => {
                const next = imageUrl.trim();
                if (next && !/^https?:\/\//.test(next)) return; // ignora URL inválida sem travar o campo
                if (next !== (node.style?.image_url || '')) commitStyle({ image_url: next });
              }}
              placeholder="https://exemplo.com/logo.png"
              disabled={readonly}
              className="h-9"
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Substitui o ícone quando preenchida. Só URLs http(s).
          </p>
        </div>

        <div>
          <Label className="mb-1.5 block">Ícone</Label>
          <div className="grid grid-cols-7 gap-1.5">
            <button
              type="button"
              onClick={() => handleIconSelect('')}
              disabled={readonly}
              title="Sem ícone"
              className={`flex h-8 w-8 items-center justify-center rounded border text-[10px] text-muted-foreground transition-colors hover:border-primary/60 disabled:pointer-events-none disabled:opacity-50 ${
                !icon ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
            {ICON_OPTIONS.map((key) => {
              const IconComp = ICON_MAP[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleIconSelect(key)}
                  disabled={readonly}
                  title={key.replace(/_/g, ' ')}
                  className={`flex h-8 w-8 items-center justify-center rounded border transition-colors hover:border-primary/60 disabled:pointer-events-none disabled:opacity-50 ${
                    icon === key ? 'border-primary bg-primary/10' : 'border-border'
                  }`}
                >
                  <IconComp className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>
        </>)}

        <div>
          <Label>Responsável</Label>
          <Select
            value={node.responsavel_node_id || 'none'}
            disabled={readonly}
            onValueChange={(v) => onChange({ responsavel_node_id: v === 'none' ? null : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sem responsável">
                {responsavelLabel || 'Sem responsável'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Sem responsável —</SelectItem>
              {pessoas.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">
            Lista carregada do Organograma da empresa.
          </p>
        </div>

        <div>
          <Label>Departamento</Label>
          <Select
            value={node.departamento_id || 'none'}
            disabled={readonly}
            onValueChange={(v) => onChange({ departamento_id: v === 'none' ? null : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sem departamento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Sem departamento —</SelectItem>
              {departamentos.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Projeto vinculado</Label>
          <Select
            value={node.cronograma_id || 'none'}
            disabled={readonly}
            onValueChange={(v) => onChange({ cronograma_id: v === 'none' ? null : v })}
          >
            {/* asChild: o próprio botão primário vira o gatilho do Select (Radix posiciona o
                popover relativo a ele), em vez do SelectTrigger padrão com aparência de campo. */}
            <SelectPrimitive.Trigger asChild disabled={readonly}>
              <Button type="button" variant="default" size="sm" disabled={readonly} className="w-full">
                {node.cronograma?.titulo || 'Vincular'}
              </Button>
            </SelectPrimitive.Trigger>
            <SelectContent>
              <SelectItem value="none">— Sem vínculo —</SelectItem>
              {projetos.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.titulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end mt-1">
            <Button
              type="button"
              variant="ghost"
              disabled={readonly || !node.cronograma_id}
              onClick={() => onChange({ cronograma_id: null })}
              title="Remover vínculo"
              className="h-8 w-8 shrink-0 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Liga este nó a um projeto do ProjetoT — aparece como referência na tela do projeto.
          </p>
        </div>
      </div>
    </aside>
  );
}
